/**
 * Error reporting service
 * Captures and reports runtime errors for monitoring
 * Ready to integrate with Sentry, Rollbar, or similar service
 */

interface ErrorReport {
  message: string;
  stack?: string;
  context?: Record<string, unknown>;
  userId?: string;
  page?: string;
  timestamp: string;
  environment: string;
  version: string;
}

const ERROR_QUEUE: ErrorReport[] = [];
const MAX_QUEUE_SIZE = 50;

/**
 * Initialize error reporting (call once on app startup)
 */
export function initializeErrorReporting(version: string, environment: string = 'production') {
  // Set up global error handler
  window.addEventListener('error', (event) => {
    captureError(event.error, {
      context: { source: 'uncaughtError', filename: event.filename, lineno: event.lineno }
    });
  });

  // Set up unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason, {
      context: { source: 'unhandledPromiseRejection' }
    });
  });

  // Store config for later use
  (window as unknown as Record<string, unknown>).__frpErrorConfig = { version, environment };
}

/**
 * Capture an error with context
 */
export function captureError(
  error: Error | unknown,
  options?: {
    context?: Record<string, unknown>;
    userId?: string;
    page?: string;
  }
) {
  const config = (window as unknown as Record<string, unknown>).__frpErrorConfig as
    | { version: string; environment: string }
    | undefined;

  const report: ErrorReport = {
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    context: options?.context,
    userId: options?.userId,
    page: options?.page || window.location.pathname,
    timestamp: new Date().toISOString(),
    environment: config?.environment || 'unknown',
    version: config?.version || 'unknown'
  };

  // Log to console in development
  if (config?.environment === 'development') {
    console.error('[ErrorReporting]', report);
  }

  // Queue error
  ERROR_QUEUE.push(report);
  if (ERROR_QUEUE.length > MAX_QUEUE_SIZE) {
    ERROR_QUEUE.shift(); // Remove oldest
  }

  // TODO: Send to error reporting service
  // await sendToErrorService(report);
}

/**
 * Get queued errors (for debugging or batch sending)
 */
export function getErrorQueue(): ErrorReport[] {
  return [...ERROR_QUEUE];
}

/**
 * Clear error queue (e.g., after successful send)
 */
export function clearErrorQueue() {
  ERROR_QUEUE.length = 0;
}

/**
 * Report a specific error to monitoring service
 * Usage: try { ... } catch (err) { reportError(err, { context: { action: 'upload' } }); }
 */
export function reportError(
  error: Error | unknown,
  options?: {
    context?: Record<string, unknown>;
    userId?: string;
    page?: string;
  }
) {
  captureError(error, options);
  // In production, could send immediately to error service
  // In queue mode, batches are sent periodically
}

/**
 * Create a wrapper for async operations with error reporting
 */
export function withErrorReporting<T>(
  operation: () => Promise<T>,
  operationName: string
): Promise<T> {
  return operation().catch((err) => {
    reportError(err, { context: { operation: operationName } });
    throw err;
  });
}
